import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import styled from 'styled-components';

import { breakpoints, useBreakpointValue } from '@notino/react-styleguide';
import { AvailabilityState } from '@notino/shared/definitions/types';

import { useFetchProductAlternatives } from '@containers/ProductDetailContainer/hooks/useFetchProductAlternatives';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useIsMounted } from '@hooks/useIsMounted';

import { useProductDetailContext } from '../../ProductDetailContext';
import { ProductsSection } from '../ProductsSection';

const MAX_PRODUCTS = 12;

type Props = {
  showOn: 'mobile' | 'desktop';
};

const Wrap = styled.div`
  margin: 3.5rem 0 2rem;
  @media (min-width: ${breakpoints.lg}) {
    margin: 4.5rem 0 0.5rem;
  }
`;

export const ProductAlternatives = ({ showOn }: Props) => {
  const isMounted = useIsMounted();
  const isDesktop = useBreakpointValue({ lg: true }, { ssrValue: false });
  const isDesiredViewPort = showOn === 'desktop' ? isDesktop : !isDesktop;
  const [ref, inView] = useInView({ triggerOnce: true });
  const { product } = useProductDetailContext();
  const { experiment_pd_outofstock_product_alternatives } = useFeatureFlags();

  const everyProductUnavailable = product.variants.every(
    (v) => v.availability.state !== AvailabilityState.CanBeBought
  );

  const { data, loading } = useFetchProductAlternatives();

  if (
    !experiment_pd_outofstock_product_alternatives ||
    !everyProductUnavailable ||
    !isMounted ||
    !isDesiredViewPort
  ) {
    return null;
  }

  return (
    <div
      id="product-alternatives"
      ref={ref}
      data-testid="product-alternatives-wrapper"
    >
      {inView && (
        <Wrap>
          <ProductsSection
            productIds={
              data?.getOutOfStockAlternatives?.recommendedProducts
                .map((x) => x.productId)
                .slice(0, MAX_PRODUCTS) ?? []
            }
            productIdsLoading={loading}
            titleMessage={{
              id: 'pd.productAlternatives.title',
              defaultMessage: 'Ostatni nakupuji',
            }}
            titleId="pdProductAlternatives"
            containerId="pd-product-alternatives-header"
            listLabel="product_alternatives"
            withDivider={false}
            trackProductClick={true}
            trackProductImpression={true}
            attributionToken={
              data?.getOutOfStockAlternatives?.recommendationAttributes
                ?.attributionToken
            }
            itemsPerSlide={3}
          />
        </Wrap>
      )}
    </div>
  );
};
